import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, Loader2, RefreshCw } from "lucide-react";
import ErrorBoundary from "../ErrorBoundary";
import { PdfToolbar, PdfThumbRail } from "./PdfViewerParts";

const clamp = (z) => Math.min(4, Math.max(0.5, z));

// 服务端页图档位（宽度 px），必须与 backend/plan_viewer.py 的 SIZES 保持一致。
const SIZE_PX = { s: 220, m: 1600, l: 2200 };

// 在位图内存里养多少张整页大图的上限，按「每页实际像素」反推，而不是按页数拍脑袋。
// 手机 40MB / 桌面 160MB：一张 ARCH D 横向图纸 m 档约 6.5MB、l 档约 12.3MB，
// 竖版大图纸 l 档能到 27.7MB，所以必须按像素算，不能按页数算。
const BUDGET_MB_NARROW = 40;
const BUDGET_MB_WIDE = 160;
const MAX_MOUNTED = 5;   // 再多也没人同时看得到，纯属浪费内存

/** 小屏判定用「短边 < 768」：手机横屏（844×390）也算小屏，笔记本（1024×768）不算。
 *  取短边而不是宽度，是因为横屏时宽度会伪装成桌面尺寸，帧率与内存却还是手机的量级。 */
const isNarrowViewport = () => typeof window !== "undefined"
  && Math.min(window.innerWidth || 0, window.innerHeight || 0) < 768;

const useNarrowViewport = () => {
  const [narrow, setNarrow] = useState(isNarrowViewport);
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onChange = () => setNarrow(isNarrowViewport());
    window.addEventListener("resize", onChange);
    window.addEventListener("orientationchange", onChange);
    return () => { window.removeEventListener("resize", onChange); window.removeEventListener("orientationchange", onChange); };
  }, []);
  return narrow;
};

/* 服务端逐页渲染的 PDF 查看器：左侧缩略图跳页 · 捏合 / Ctrl+滚轮 / 按钮缩放 · 全屏（原生 API，iPhone 回退为覆盖层）。
 *
 * 需求来源（2026-11-04 客户 Cara Liu：「我在手机上每次放大图纸就会出错」，附截图：
 *   「A problem repeatedly occurred on "https://aliadu.com/board?project=P017"」）：
 *   这句是 iOS Safari / 微信 WKWebView 的 web 进程被系统内存回收后的标准提示 —— 不是业务报错，
 *   JS 层的 ErrorBoundary 也接不住（整进程被杀，不是渲染异常）。触发点就是「放大」：
 *   浏览器要按新比例重新光栅化整屏内容，正是内存峰值时刻。
 *   客户项目 P017 的批准图纸实测为 23 页 ARCH D（2592×1728pt），服务端按 2200px 宽渲染后
 *   单页约 12MB 位图，23 页全挂进 DOM 就是 74M 像素 / 约 283MB 位图；再加上看板页自身内存，
 *   远超 iOS 下 WKWebView 的预算。桌面浏览器内存充足（实测同样挂 24 张、解码 360MB 也不报错），
 *   所以只在手机复现。
 *
 * 因此这里的三条约束都不依赖「在真机上刚好没崩」，而是从结构上把内存封住：
 *   1) 档位：小屏只要 1600px 的 m 档（位图约为 l 档的 1/2，实测一张 ARCH D 从 27.7MB 降到 14.6MB），
 *      桌面仍用 2200px 的 l 档；
 *   2) 挂载：只挂当前页附近的整页大图，且总量受「位图预算」约束（手机 40MB），与总页数脱钩；
 *      窗口外的页保留同比例白底占位，滚动位置、页码统计、跳页、全屏、缩略图行为都不变；
 *   3) 兜底：单页首次失败自动重试一次（服务端 pdftoppm 偶发超时/排队），仍失败则显示「重试」按钮，
 *      并在顶部汇总「N 页加载失败 + 全部重试 + 下载原件」；渲染期异常由局部 ErrorBoundary 接住，
 *      只坏这一块，不拖垮整页。
 */
const PdfViewerInner = ({ meta, pageUrl, downloadUrl, zh, extra, tp = "pdf", initialThumbnails }) => {
  const N = meta.pageCount;
  const narrow = useNarrowViewport();
  // 小屏取 m 档：390~430px 宽的手机放到 400% 时可视宽度约 1600px，正好对上 m 档，不失真也不爆内存。
  const size = narrow ? "m" : "l";
  const [page, setPage] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [thumbs, setThumbs] = useState(() => initialThumbnails ?? (typeof window === "undefined" || window.innerWidth >= 640));
  const [fs, setFs] = useState(false);
  const [pseudo, setPseudo] = useState(false);
  const [st, setSt] = useState({});
  // 重试计数：既用于 <img> 的 key（强制重新挂载）也用于给 URL 加 r 参数绕过缓存。
  const [attempt, setAttempt] = useState({});
  const attemptsRef = useRef({});
  const rootRef = useRef(null);
  const scrollRef = useRef(null);
  const zoomRef = useRef(1);
  const skipRef = useRef(0);
  zoomRef.current = zoom;

  const bumpAttempt = useCallback((n) => {
    const next = { ...attemptsRef.current, [n]: (attemptsRef.current[n] || 0) + 1 };
    attemptsRef.current = next;
    setAttempt(next);
  }, []);

  const clearPageState = useCallback((n) => {
    setSt((m) => { const next = { ...m }; delete next[n]; return next; });
  }, []);

  // 首次失败先自动重试一次（大图纸的 pdftoppm 偶发超时/排队很常见，用户不该为此点两下），
  // 第二次仍失败才停在「渲染失败 + 重试」上，避免对真正的 4xx/5xx 无限重试打满服务端。
  const onPageError = useCallback((n) => {
    if ((attemptsRef.current[n] || 0) === 0) { bumpAttempt(n); return; }
    setSt((m) => ({ ...m, [n]: "err" }));
  }, [bumpAttempt]);

  const retryPage = useCallback((n) => { clearPageState(n); bumpAttempt(n); }, [bumpAttempt, clearPageState]);

  const failed = useMemo(() => Object.keys(st).filter((n) => st[n] === "err"), [st]);
  const retryAll = useCallback(() => { failed.forEach((n) => retryPage(Number(n))); }, [failed, retryPage]);

  const goTo = useCallback((n) => {
    const el = scrollRef.current?.querySelector(`[data-page="${n}"]`);
    skipRef.current = Date.now() + 400;
    if (el && scrollRef.current) scrollRef.current.scrollTo({ top: el.offsetTop - 8, behavior: "auto" });
    setPage(n);
  }, []);

  const probePage = useCallback(() => {
    const c = scrollRef.current; if (!c) return;
    const probe = c.getBoundingClientRect().top + c.clientHeight * 0.4;
    let best = 1, bestD = Infinity;
    c.querySelectorAll("[data-page]").forEach((el) => {
      const r = el.getBoundingClientRect();
      const d = r.top <= probe && r.bottom >= probe ? 0 : Math.min(Math.abs(r.top - probe), Math.abs(r.bottom - probe));
      if (d < bestD) { bestD = d; best = Number(el.dataset.page); }
    });
    setPage(best);
  }, []);

  const onScroll = () => {
    if (Date.now() < skipRef.current) return;
    probePage();
  };

  // 缩放 / 进出全屏会改变页高，但不会触发 scroll 事件。不补一次定位，页码与挂载窗口会停在旧位置，
  // 表现为「放大后当前页的图没跟着换、后面的页一片空白」。捏合时 zoom 每个 touchmove 都变，
  // 这里延后一点再算，避免手势过程中反复测量每页的页框拖慢低端机。
  useEffect(() => {
    const t = setTimeout(probePage, 120);
    return () => clearTimeout(t);
  }, [zoom, pseudo, probePage]);

  // 按位图预算算挂载窗口：从当前页前一页开始向后纳入，直到超预算或页数上限。
  // 这样「一堆小页的合同」能多挂几页、「一张顶十页的大图纸」少挂几页，都不靠人工配阈值。
  const { from, to } = useMemo(() => {
    const budget = narrow ? BUDGET_MB_NARROW : BUDGET_MB_WIDE;
    const width = SIZE_PX[size];
    const picked = [];
    let used = 0;
    for (let n = Math.max(1, page - 1); n <= N && picked.length < MAX_MOUNTED; n += 1) {
      const [w, h] = meta.sizes[n - 1] || [0, 0];
      // 解码后的位图按 RGBA 4 字节/像素估算；尺寸异常时按 l 档兜底，宁少挂一页也别多挂。
      const mb = w > 0 && h > 0 ? (width * (width * h / w) * 4) / 1048576 : SIZE_PX.l * SIZE_PX.l * 4 / 1048576;
      if (picked.length > 0 && used + mb > budget) break;
      picked.push(n);
      used += mb;
    }
    if (!picked.length) picked.push(page);
    return { from: picked[0], to: picked[picked.length - 1] };
  }, [page, N, narrow, size, meta.sizes]);

  useEffect(() => {
    const c = scrollRef.current; if (!c) return undefined;
    let d0 = 0, z0 = 1;
    const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
    const ts = (e) => { if (e.touches.length === 2) { d0 = dist(e.touches); z0 = zoomRef.current; } };
    const tm = (e) => { if (e.touches.length === 2 && d0) { e.preventDefault(); setZoom(clamp(z0 * dist(e.touches) / d0)); } };
    const te = () => { d0 = 0; };
    const wh = (e) => { if (e.ctrlKey || e.metaKey) { e.preventDefault(); setZoom((z) => clamp(z * (e.deltaY < 0 ? 1.1 : 0.9))); } };
    c.addEventListener("touchstart", ts, { passive: true }); c.addEventListener("touchmove", tm, { passive: false });
    c.addEventListener("touchend", te); c.addEventListener("wheel", wh, { passive: false });
    return () => { c.removeEventListener("touchstart", ts); c.removeEventListener("touchmove", tm); c.removeEventListener("touchend", te); c.removeEventListener("wheel", wh); };
  }, [pseudo]);

  useEffect(() => {
    const h = () => setFs(!!(document.fullscreenElement || document.webkitFullscreenElement));
    document.addEventListener("fullscreenchange", h); document.addEventListener("webkitfullscreenchange", h);
    return () => { document.removeEventListener("fullscreenchange", h); document.removeEventListener("webkitfullscreenchange", h); };
  }, []);

  const toggleFs = () => {
    if (pseudo) { setPseudo(false); return; }
    if (document.fullscreenElement || document.webkitFullscreenElement) { (document.exitFullscreen || document.webkitExitFullscreen).call(document); return; }
    const el = rootRef.current;
    if (el?.requestFullscreen) el.requestFullscreen().catch(() => setPseudo(true));
    else if (el?.webkitRequestFullscreen) el.webkitRequestFullscreen();
    else setPseudo(true);
  };

  const body = (
    <div ref={rootRef} data-testid={`${tp}-viewer`} className={`flex flex-col bg-slate-900 text-slate-100 overflow-hidden select-none ${pseudo ? "fixed inset-0 z-[10000]" : "h-full rounded-xl"}`}>
      <PdfToolbar zh={zh} page={page} total={N} zoom={zoom} thumbs={thumbs} fs={fs || pseudo} extra={extra} downloadUrl={downloadUrl} tp={tp}
        onPrev={() => goTo(Math.max(1, page - 1))} onNext={() => goTo(Math.min(N, page + 1))} onThumbs={() => setThumbs((v) => !v)}
        onZoomIn={() => setZoom((z) => clamp(z * 1.25))} onZoomOut={() => setZoom((z) => clamp(z / 1.25))} onFit={() => setZoom(1)} onFs={toggleFs} />
      {failed.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 border-b border-amber-300/40 bg-amber-100/95 px-2 py-1 text-[11px] text-amber-900" data-testid={`${tp}-page-errors`} role="status">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>{zh ? `${failed.length} 页加载失败` : `${failed.length} page(s) failed to load`}</span>
          <button type="button" onClick={retryAll} data-testid={`${tp}-retry-all`} className="inline-flex items-center gap-1 rounded border border-amber-600/50 px-1.5 py-0.5 font-semibold hover:bg-amber-200"><RefreshCw className="h-3 w-3" aria-hidden="true" />{zh ? "全部重试" : "Retry all"}</button>
          {downloadUrl && <a href={downloadUrl} target="_blank" rel="noreferrer" data-testid={`${tp}-page-errors-download`} className="underline">{zh ? "或下载原件" : "or download original"}</a>}
        </div>
      )}
      <div className="flex flex-1 min-h-0">
        {thumbs && <PdfThumbRail meta={meta} pageUrl={pageUrl} page={page} goTo={goTo} tp={tp} />}
        <div ref={scrollRef} onScroll={onScroll} data-testid={`${tp}-scroll`} className="relative flex-1 overflow-auto bg-slate-800/60 p-2 sm:p-4" style={{ touchAction: "pan-x pan-y" }}>
          {meta.sizes.map(([w, h], i) => {
            const n = i + 1;
            // 窗口外的页只留占位：div 本身带着 aspectRatio，高度与挂载时完全一致，所以滚动几何不变。
            const mounted = n >= from && n <= to;
            const err = st[n] === "err";
            // 重试时给 URL 加 r 参数绕过缓存；data: URL（如财务预告逐页内嵌图）不带查询串，不能拼。
            const pageSrc = pageUrl(n, size);
            const src = attempt[n] && pageSrc.includes("?") ? `${pageSrc}&r=${attempt[n]}` : pageSrc;
            return (
              <div key={i} data-page={n} data-mounted={mounted ? "1" : "0"} className="relative mx-auto mb-3 bg-white shadow-lg shadow-black/40" style={{ width: `${zoom * 100}%`, aspectRatio: `${w} / ${h}` }}>
                {mounted && <>
                  <span className={`absolute inset-0 flex items-center justify-center gap-2 px-2 text-center text-xs ${err ? "text-red-500" : "text-slate-400"}`} data-testid={`${tp}-page-state-${n}`}>
                    {err ? <>
                      <span>{zh ? `第 ${n} 页渲染失败` : `Page ${n} failed to render`}</span>
                      <button type="button" onClick={() => retryPage(n)} data-testid={`${tp}-page-retry-${n}`} className="rounded border border-current px-1.5 py-0.5 text-[11px] font-semibold hover:bg-red-50">{zh ? "重试" : "Retry"}</button>
                    </> : <><Loader2 className="h-4 w-4 animate-spin" />{zh ? `第 ${n} 页` : `Page ${n}`}</>}
                  </span>
                  <img key={`${n}-${attempt[n] || 0}`} src={src} alt={`page ${n}`} loading="lazy" decoding="async" draggable={false}
                    onLoad={() => setSt((m) => ({ ...m, [n]: "ok" }))} onError={() => onPageError(n)}
                    className={`relative w-full h-full block transition-opacity duration-300 ${st[n] === "ok" ? "opacity-100" : "opacity-0"}`} data-testid={`${tp}-page-${n}`} />
                </>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
  return pseudo ? createPortal(body, document.body) : body;
};

/** 局部错误边界：查看器自身渲染异常只坏这一块（并给出重试 / 下载原件），不把整页拖成白屏。
 *  注意它接不住「web 进程被系统回收」那种整进程崩溃 —— 那是内存问题，只能靠上面的预算封住。 */
export const PdfViewer = (props) => (
  <ErrorBoundary fallback={(error, reset) => (
    <div className="flex h-full flex-col items-center justify-center gap-2 rounded-xl bg-slate-900 p-4 text-center text-slate-200" data-testid={`${props.tp || "pdf"}-viewer-error`}>
      <AlertTriangle className="h-6 w-6 text-amber-400" aria-hidden="true" />
      <p className="text-sm font-semibold">{props.zh ? "图纸预览出错" : "Drawing preview error"}</p>
      <p className="max-w-sm break-all text-[11px] text-slate-400">{String(error?.message || error)}</p>
      <button type="button" onClick={reset} data-testid={`${props.tp || "pdf"}-viewer-error-retry`} className="rounded-md bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-600">{props.zh ? "重试" : "Retry"}</button>
      {props.downloadUrl && <a href={props.downloadUrl} target="_blank" rel="noreferrer" className="text-xs text-brand-300 underline">{props.zh ? "下载原件" : "Download original"}</a>}
    </div>
  )}>
    <PdfViewerInner {...props} />
  </ErrorBoundary>
);
